/**
 * CollectionLogModal — Bitácora de gestiones de cobranza.
 * Muestra timeline de gestiones y permite registrar nuevas.
 */
import { useState } from 'react'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { fmtDate } from '@/lib/utils'
import { useTenantLocale } from '@/lib/locale'
import {
  Phone, Mail, MessageSquare, MapPin, FileText, Handshake, MoreHorizontal,
  Plus, Loader2, Trash2, User, Calendar, ClipboardList,
} from 'lucide-react'
import {
  useCollectionLogs, useCreateCollectionLog, useDeleteCollectionLog,
  type CollectionLog,
} from '../hooks/useSales'
import DatePicker from '@/components/DatePicker'
import { Modal } from '@/components/ds/Modal'

const ACTION_TYPES = [
  { value: 'call', label: 'Llamada', icon: Phone },
  { value: 'email', label: 'Correo', icon: Mail },
  { value: 'message', label: 'Mensaje', icon: MessageSquare },
  { value: 'visit', label: 'Visita', icon: MapPin },
  { value: 'letter', label: 'Carta formal', icon: FileText },
  { value: 'agreement', label: 'Acuerdo de pago', icon: Handshake },
  { value: 'other', label: 'Otro', icon: MoreHorizontal },
] as const

const ACTION_ICON_MAP: Record<string, typeof Phone> = Object.fromEntries(
  ACTION_TYPES.map(a => [a.value, a.icon])
)

interface Props {
  documentId: string
  customerName: string
  folio: string | null
  outstanding: string
  onClose: () => void
}

export function CollectionLogModal({ documentId, customerName, folio, outstanding, onClose }: Props) {
  const locale = useTenantLocale()
  const { data: logs, isLoading } = useCollectionLogs(documentId)
  const createLog = useCreateCollectionLog()
  const deleteLog = useDeleteCollectionLog()
  const { confirm: confirmAction, modalProps } = useConfirmModal()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    action_type: 'call' as CollectionLog['action_type'],
    description: '',
    contact_name: '',
    result: '',
    next_action_date: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.description.trim()) return
    createLog.mutate(
      { documentId, data: { ...form, next_action_date: form.next_action_date || null } },
      {
        onSuccess: () => {
          setForm({ action_type: 'call' as CollectionLog['action_type'], description: '', contact_name: '', result: '', next_action_date: '' })
          setShowForm(false)
        },
      }
    )
  }

  const handleDelete = (logId: string) => {
    confirmAction({
      message: '¿Eliminar esta gestión?',
      variant: 'danger',
      onConfirm: () => deleteLog.mutate({ documentId, logId }),
    })
  }

  const inputCls = 'w-full rounded-md border border-divider px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none'

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={
          <span className="flex items-center gap-2">
            <ClipboardList size={20} className="text-primary" />
            Gestiones de Cobranza
          </span>
        }
        subtitle={`${customerName} ${folio ? `| Folio #${folio}` : ''} | Pendiente: $${outstanding}`}
      >
          <div className="space-y-4">
            {/* Add button */}
            {!showForm && (
              <button onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors">
                <Plus size={14} />Registrar gestión
              </button>
            )}

            {/* New log form */}
            {showForm && (
              <form onSubmit={handleSubmit} className="bg-glass-3 rounded-lg p-4 space-y-3 border border-divider">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="collectionlogmodal-action-type" className="block text-xs font-medium text-content-muted mb-1">Tipo de gestión</label>
                    <select id="collectionlogmodal-action-type" value={form.action_type} onChange={e => setForm(f => ({ ...f, action_type: e.target.value as CollectionLog['action_type'] }))} className={inputCls}>
                      {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="collectionlogmodal-contact-name" className="block text-xs font-medium text-content-muted mb-1">Persona contactada</label>
                    <input id="collectionlogmodal-contact-name" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                      placeholder="Nombre del contacto" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="collectionlogmodal-description" className="block text-xs font-medium text-content-muted mb-1">Descripción *</label>
                  <textarea id="collectionlogmodal-description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    rows={2} placeholder="Detalle de la gestión realizada..." className={inputCls} required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="collectionlogmodal-result" className="block text-xs font-medium text-content-muted mb-1">Resultado</label>
                    <input id="collectionlogmodal-result" value={form.result} onChange={e => setForm(f => ({ ...f, result: e.target.value }))}
                      placeholder="Ej: Se comprometió a pagar..." className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="collectionlogmodal-next-action-date" className="block text-xs font-medium text-content-muted mb-1">Próximo seguimiento</label>
                    <DatePicker id="collectionlogmodal-next-action-date" value={form.next_action_date} onChange={(v: string) => setForm(f => ({ ...f, next_action_date: v }))} />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-3 py-1.5 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">Cancelar</button>
                  <button type="submit" disabled={createLog.isPending || !form.description.trim()}
                    className="px-3 py-1.5 text-sm text-on-primary bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 inline-flex items-center gap-1.5">
                    {createLog.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                    Registrar
                  </button>
                </div>
              </form>
            )}

            {/* Timeline */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-content-subtle">
                <Loader2 size={18} className="animate-spin mr-2" />Cargando...
              </div>
            ) : !logs?.length ? (
              <div className="text-center py-8">
                <ClipboardList size={28} className="mx-auto text-content-subtle mb-2" />
                <p className="text-sm text-content-muted">Sin gestiones registradas</p>
                <p className="text-xs text-content-subtle mt-0.5">Registre llamadas, correos y otras acciones de cobranza.</p>
              </div>
            ) : (
              <div className="space-y-0">
                {logs.map((log, idx) => {
                  const Icon = ACTION_ICON_MAP[log.action_type] || MoreHorizontal
                  return (
                    <div key={log.id} className="relative pl-8 pb-4">
                      {/* Timeline line */}
                      {idx < logs.length - 1 && (
                        <div className="absolute left-[13px] top-7 bottom-0 w-px bg-surface-muted" />
                      )}
                      {/* Icon */}
                      <div className="absolute left-0 top-0.5 w-7 h-7 rounded-full bg-primary-50 border border-primary-200 flex items-center justify-center">
                        <Icon size={13} className="text-primary" />
                      </div>
                      {/* Content */}
                      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-3 shadow-card">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="text-sm font-medium text-content">{log.action_type_display}</span>
                            {log.contact_name && (
                              <span className="text-xs text-content-subtle ml-2 inline-flex items-center gap-0.5">
                                <User size={10} />{log.contact_name}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-content-subtle">{fmtDate(log.created_at, locale)}</span>
                            <button onClick={() => handleDelete(log.id)} title="Eliminar"
                              className="p-1 rounded hover:bg-danger-surface text-content-subtle hover:text-danger-solid transition-colors">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-content-muted mt-1">{log.description}</p>
                        {(log.result || log.next_action_date) && (
                          <div className="mt-2 flex items-center gap-4 text-xs text-content-muted">
                            {log.result && <span className="bg-surface-muted px-2 py-0.5 rounded">{log.result}</span>}
                            {log.next_action_date && (
                              <span className="inline-flex items-center gap-1">
                                <Calendar size={10} />Seguimiento: {fmtDate(log.next_action_date, locale)}
                              </span>
                            )}
                          </div>
                        )}
                        {log.created_by_name && (
                          <p className="text-xs text-content-subtle mt-1">por {log.created_by_name}</p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
      </Modal>
      <ConfirmModal {...modalProps} />
    </>
  )
}
